import i18next from 'i18next';
import { List } from 'immutable';
import * as React from 'react';
import { StandardLocalWidget, any, fromJSX, loadedAsyncState, loadingAsyncState } from "widgets-for-react";
import { changePasswordWidget } from "../../forms/changePassword/changePasswordWidget";
import { companyWidget } from "../../forms/company/companyWidget";
import { emptyCountry } from '../../forms/country/countryState';
import { invoiceSelectorWidget } from '../../forms/invoiceSelector/invoiceSelectorWidget';
import { personalInfoWidget } from '../../forms/personalInfo/personalInfoWidget';
import { subscriptionSelectorWidget } from "../../forms/subscriptionSelector/subscriptionSelectorWidget";
import { RootLayout } from "../../site/root/rootLayout";
import { authOptions } from '../../site/root/rootWidget';
import { routeUpdaters } from "../../site/root/routes/routesState";
import { formatDate } from "../../utils/date";
import { FormControls } from '../../utils/forms';
import { singleValidatorResultBuilders } from "../../validator/validator";
import { EUCountriesAndUK, validateVAT } from '../newSubscription/newSubscriptionUtils';
import { SubscriptionLayout } from "../subscriptionLayout";
import { SubscriptionState, subscriptionUpdaters } from "../subscriptionState";
import { formatSubscriptionPrice } from '../subscriptionUtils';
import { initializePIIData, PIIData } from 'utils/datalayer';

// Old widget that works with the Hoppinger subscriptions
export const legacyAccountDetailsPageWidget: StandardLocalWidget<SubscriptionState> = currentState => {
  const isTrial = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.ActiveSubscription.length >= 1 && currentState.userSubscriptions.value.ActiveSubscription[0].Subscription.Price <= 0
  const willBeStopped = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "StillValidTerminatedSubscription"
  const studentSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidStudentSubscription"
  const studentSubscriptionWithPausedSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidStudentSubscriptionWithPausedSubscription"
  const isStudent = studentSubscription || studentSubscriptionWithPausedSubscription
  const teacherSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidTeacherSubscription"
  const teacherSubscriptionWithPausedSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PaywallStatus == "ValidTeacherSubscriptionWithPausedSubscription"

  const isStoppedOrNone = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.ActiveSubscription.length <= 0
  const currentSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.ActiveSubscription.length > 0 ? currentState.userSubscriptions.value.ActiveSubscription[0].Subscription : undefined
  const currentActiveSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.ActiveSubscription.length > 0 ? currentState.userSubscriptions.value.ActiveSubscription[0] : undefined
  const currentPausedSubscription = currentState.userSubscriptions.kind == 'loaded' && currentState.userSubscriptions.value.PausedSubscriptions.length > 0 ? currentState.userSubscriptions.value.PausedSubscriptions[0].Subscription : undefined

  const piiData = initializePIIData(currentState.userSubscriptions);

  return any( [fromJSX(setState =>
    currentState.userSubscriptions.kind != "loaded"
    // This should never happen because the subscriptionWidget already checks for this
    ? <RootLayout.Loader showText hasWrapper />
    : 
    <SubscriptionLayout.Wrapper
        id="test-accountManagement"
        breadcrumbs={List([{ url: routeUpdaters(i18next.language).dashboard.url, label: 'dashboard' }])}
        >
        <SubscriptionLayout.Section title={i18next.t('subscription:YourAccount')}>
            {/* ----- Account type */}
            {willBeStopped
            ? <SubscriptionLayout.AccountType
                kind="will-be-stopped"
                intro={<p>{i18next.t("subscription:WillBeStoppedSubscriptionIntro", {
                terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.kind == "loaded" ? currentState.userSubscriptions.value.TerminationDate : Date.now())
                })}</p>}
            />
            : studentSubscription
                ? <SubscriptionLayout.AccountType
                kind="student"
                intro={<p>{i18next.t("subscription:StudentSubscriptionIntro", {
                    terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.kind == "loaded" ? currentState.userSubscriptions.value.TerminationDate : Date.now()),
                })}</p>}
                />
                : studentSubscriptionWithPausedSubscription
                ? <SubscriptionLayout.AccountType
                    kind="student-paused-pro"
                    intro={<p>{i18next.t("subscription:StudentSubscriptionWithPausedActiveAccountIntro", {
                    terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.kind == "loaded" ? currentState.userSubscriptions.value.TerminationDate : Date.now()),
                    paymentFrequency: currentState.userSubscriptions.kind == "loaded" && i18next.t(`subscription:${currentPausedSubscription?.PaymentFrequency == 'Monthly' ? "monthly" : "yearly"}`),
                    })}</p>}
                />
                : teacherSubscription
                    ? <SubscriptionLayout.AccountType
                    kind="teacher"
                    intro={<p>{i18next.t("subscription:TeacherSubscriptionIntro", {
                        terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.kind == "loaded" ? currentState.userSubscriptions.value.TerminationDate : Date.now()),
                    })}</p>}
                    />
                    : teacherSubscriptionWithPausedSubscription
                    ? <SubscriptionLayout.AccountType
                        kind="teacher-paused-pro"
                        intro={<p>{i18next.t("subscription:TeacherSubscriptionWithPausedActiveAccountIntro", {
                        terminationDate: formatDate.DDMonthYYYY(i18next, currentState.userSubscriptions.kind == "loaded" ? currentState.userSubscriptions.value.TerminationDate : Date.now()),
                        paymentFrequency: currentState.userSubscriptions.kind == "loaded" && i18next.t(`subscription:${currentPausedSubscription?.PaymentFrequency == 'Monthly' ? "monthly" : "yearly"}`),
                        })}</p>}
                    />
                    : isStoppedOrNone
                        ? <SubscriptionLayout.AccountType
                        kind="stopped-or-none"
                        intro={<p>{i18next.t("subscription:StoppedSubscriptionIntro")}</p>}
                        />
                        : isTrial
                        ? <SubscriptionLayout.AccountType
                            kind="trial"
                            intro={
                            <p>
                                {i18next.t("subscription:TrialSubscriptionIntro", { daysLeft: Math.ceil(currentState.userSubscriptions.value.TrialDaysLeft) })}
                            </p>
                            }
                        />
                        : <SubscriptionLayout.AccountType
                            kind="pro"
                            intro={
                            <p>
                                {i18next.t("subscription:ProSubscriptionIntro", {
                                paymentFrequency: currentState.userSubscriptions.kind == "loaded" && i18next.t(`subscription:${currentSubscription?.CurrencyPaymentFrequency == "Monthly" ? "monthly" : "yearly"}`),
                                amount: currentSubscription?.Price ? formatSubscriptionPrice(currentSubscription.Price, i18next.language) : 0,
                                currency: currentSubscription?.Currency || "€",
                                nextPayment: formatDate.DDMonthYYYY(i18next, currentActiveSubscription?.RecurrentPayment.NextPaymentDate || Date.now())
                                })}
                            </p>
                            }
                        />
            }

            {/* ----- Open invoices */}
            {currentState.userSubscriptions.value.OpenPayments.length > 0
            ? <SubscriptionLayout.OpenInvoice options={authOptions}>
                {currentState.userSubscriptions.value.OpenPayments.map(openPayment =>
                <SubscriptionLayout.OpenInvoiceItem
                    openPayment={openPayment}
                    onPay={() => setState(subscriptionUpdaters.startPayOpenPaymentInputFlow(openPayment))}
                />
                )}
            </SubscriptionLayout.OpenInvoice>
            : <></>}
        </SubscriptionLayout.Section>


        {/* ----- Settings */}
        <SubscriptionLayout.Section title={i18next.t('subscription:Settings')} suppressHotjar={true}>
            {/* E-mail */}
            <SubscriptionLayout.Email email={currentState.userSubscriptions.value.User?.Email} />

            {/* Change password */}
            <SubscriptionLayout.ChangePassword
            onSubmit={currentState.editing?.kind == "password"
                ? () => setState(subscriptionUpdaters.invokeChangePasswordApi)
                : undefined}
            onClick={currentState.editing?.kind != "password"
                ? () => setState(subscriptionUpdaters.startChangePasswordSubFlow())
                : undefined}
            >
            {currentState.editing?.kind == "password"
                ? <>
                <p>{i18next.t("auth:changePwPrompt")}</p>
                {changePasswordWidget(currentState.editing.changePasswordState)
                    .map(subscriptionUpdaters.changePasswordState)
                    .run(setState)}
                <SubscriptionLayout.ChangePasswordButtons
                    cancel={() => setState(subscriptionUpdaters.cancelSubFlow())}
                    save={() => setState(subscriptionUpdaters.invokeChangePasswordApi)}
                    changePasswordDisabled={!currentState.editing.changePasswordState.isValid()}
                />
                </>
                : <SubscriptionLayout.ChangePasswordPlaceholder
                edit={() => setState(subscriptionUpdaters.startChangePasswordSubFlow())}
                />
            }
            </SubscriptionLayout.ChangePassword>

            {/* Personal info */}
            <SubscriptionLayout.PersonalInfo
            onSubmit={currentState.editing?.kind == "personal info"
                ? () => setState(subscriptionUpdaters.invokeChangePersonalInfoApi)
                : undefined}
            onClick={currentState.editing?.kind != "personal info"
                ? () => setState(subscriptionUpdaters.startChangePersonalInfoSubFlow())
                : undefined}
            >
            {currentState.editing?.kind == "personal info"
                ? <>
                {currentState.lookupData.kind == "loaded" ?
                    personalInfoWidget(currentState.lookupData.value.Languages, currentState.lookupData.value.Countries, currentState.lookupData.value.Jobs, false, currentState.userSubscriptions.value.User.Country !== null, false, isStudent, true)(currentState.editing.personalInfoState)
                    .map(subscriptionUpdaters.changePersonalInfoPersonalInfoState)
                    .run(setState)
                    : <RootLayout.Loader showText hasWrapper />}
                {!isStudent && (
                    <>
                    <FormControls.FormRadioInGroup
                        name="subscriptionInvoiceAddress"
                        legend={i18next.t("subscription:InvoiceToCompany")}
                        required={true}
                        validation={singleValidatorResultBuilders.validating("")}
                        items={[
                        {
                            controlId: "subscriptionInvoiceAddressDifferent",
                            label: i18next.t("subscription:InvoiceWithCompany"),
                            checked: currentState.editing.invoiceToCompany || false,
                            onChange: newValue => setState(subscriptionUpdaters.changePersonalInfoInvoiceToCompany(_ => newValue)),
                        },
                        {
                            controlId: "subscriptionInvoiceAddressSame",
                            label: i18next.t("subscription:InvoiceWithoutCompany"),
                            checked: !currentState.editing.invoiceToCompany || false,
                            onChange: newValue => setState(subscriptionUpdaters.changePersonalInfoInvoiceToCompany(_ => !newValue)),
                        }
                        ]}
                    />

                    {currentState.editing.invoiceToCompany ?
                        currentState.lookupData.kind == "loaded" ?
                        companyWidget(
                            currentState.lookupData.value.Countries,
                            currentState.lookupData.value.CompanyTypes,
                            false,
                            currentState.userSubscriptions.kind === "loaded" && currentState.userSubscriptions.value.User.Country !== null,
                            true,
                            true,
                            undefined,
                            currentState.checkVATState
                        )(currentState.editing.companyState)
                            .map(subscriptionUpdaters.changePersonalInfoCompanyState)
                            .run(setState)
                        : <RootLayout.Loader showText hasWrapper />
                        : <></>}
                    </>
                )}


                <SubscriptionLayout.PersonalInfoButtonsWithRouter
                    cancel={() => setState(subscriptionUpdaters.cancelSubFlow())}
                    save={() => {                            
                    const editing = currentState.editing
                    if (editing?.kind == "personal info" && currentState.lookupData.kind == "loaded") {
                        const availableCountries = currentState.lookupData.value.Countries
                        const countryId = editing.companyState.country.input.Id
                        const country = (availableCountries.find(s => s.Id == countryId))?.Title || emptyCountry.Title || ''
                            
                        const VATnumber = editing?.companyState.input.VAT

                        setState(
                        subscriptionUpdaters.checkVATState(() =>
                            editing.invoiceToCompany && 
                            country && EUCountriesAndUK.includes(country)
                                ? loadingAsyncState(() =>
                                    validateVAT(
                                    countryId || 0,
                                    VATnumber || ''
                                    )
                                )
                                : loadedAsyncState(true)
                            )
                        )
                    }
                    if (currentState.editing?.kind == "personal info" && currentState.editing.personalInfoState.languageState.input.Code && i18next.language !== currentState.editing.personalInfoState.languageState.input.Code) {
                        i18next.changeLanguage(currentState.editing.personalInfoState.languageState.input.Code)
                    }
                    }}
                    onSaveGoTo={
                    currentState.editing?.kind == "personal info" && currentState.editing.personalInfoState.languageState.input.Code && i18next.language !== currentState.editing.personalInfoState.languageState.input.Code
                        ? routeUpdaters(currentState.editing.personalInfoState.languageState.input.Code).accountDetails.url
                        : undefined
                    }
                    saveDisabled={!currentState.editing.personalInfoState.isValid(true) || (currentState.editing.invoiceToCompany && !currentState.editing.companyState.isValid())}
                />
                </>
                : currentState.lookupData.kind == "loaded" ?
                <SubscriptionLayout.PersonalInfoPlaceholder
                    userSubscriptions={currentState.userSubscriptions.value}
                    lookupData={currentState.lookupData.value}
                    edit={() => setState(subscriptionUpdaters.startChangePersonalInfoSubFlow())}
                    isStudent={isStudent}
                />
                : <RootLayout.Loader showText hasWrapper />
            }
            </SubscriptionLayout.PersonalInfo>
        </SubscriptionLayout.Section>

        {/* ----- Change account */}
        <SubscriptionLayout.Section title={i18next.t('subscription:ChangeYourAccount')}>
            {isTrial || currentState.userSubscriptions.value.ActiveSubscription.length <= 0 ? <></> :
            <>
                {/* Change payment period */}
                <SubscriptionLayout.ChangePaymentPeriod
                onSubmit={currentState.editing?.kind == "subscription"
                    ? () => setState(subscriptionUpdaters.invokeChangeSubscriptionApi)
                    : undefined}
                onClick={currentState.editing?.kind != "subscription"
                    ? () =>
                    currentState.userSubscriptions.kind == "loaded" && currentState.userSubscriptions.value.ActiveSubscription.length > 0 ?
                        setState(subscriptionUpdaters.startChangeSubscriptionSubFlow(currentState.userSubscriptions.value.ActiveSubscription[0]))
                        : undefined
                    : undefined}
                >
                {currentState.editing?.kind == "subscription" ? (
                    currentState.lookupData.kind == "loaded" ? (
                    <>
                        {subscriptionSelectorWidget(currentState.lookupData.value.Subscriptions, 'account-details', piiData)(currentState.editing.subscriptionSelectorState)
                            .map(subscriptionUpdaters.changeSubscriptionState)
                            .run(setState)}

                        {i18next.language === 'nl' ? <p>Tijdelijk niet beschikbaar</p> : <p>Current unavailable</p>}

                        <SubscriptionLayout.ChangePaymentPeriodButtons
                        cancel={() => setState(subscriptionUpdaters.cancelSubFlow())}
                        save={() => setState(subscriptionUpdaters.invokeChangeSubscriptionApi)}
                        saveDisabled={true} //{!currentState.editing.subscriptionSelectorState.isValid() || !currentState.userSubscriptions.value.HasOriginalTransactionKey}
                        />
                    </>
                    ) : (
                    <RootLayout.Loader showText hasWrapper />
                    )
                ) : (
                    <SubscriptionLayout.ChangePaymentPeriodPlaceholder
                    options={authOptions}
                    paymentMethodInvalid={currentState.userSubscriptions.value.HasOriginalTransactionKey == false}
                    amount={currentSubscription?.Price || 0}
                    currency={currentSubscription?.Currency || "€"}
                    nextPayment={currentActiveSubscription?.RecurrentPayment.NextPaymentDate || ""}
                    paymentFrequency={currentSubscription?.CurrencyPaymentFrequency == "Monthly" ? i18next.t('subscription:month') : i18next.t('subscription:year')}
                    edit={() =>
                        currentState.userSubscriptions.kind == "loaded" && currentState.userSubscriptions.value.ActiveSubscription.length > 0 ?
                        setState(subscriptionUpdaters.startChangeSubscriptionSubFlow(currentState.userSubscriptions.value.ActiveSubscription[0]))
                        : undefined}
                    />
                )}
                </SubscriptionLayout.ChangePaymentPeriod>

                {/* Change payment method */}
                <SubscriptionLayout.ChangePaymentMethod
                paymentMethodInvalid={currentState.userSubscriptions.value.HasOriginalTransactionKey == false}
                edit={() => setState(subscriptionUpdaters.startChangePaymentMethodFlow())}
                />

                {/* Stop subscription */}
                <SubscriptionLayout.StopCurrentSubscription
                to={routeUpdaters(i18next.language).subscriptionStopPayment.url}
                />
            </>
            }

            {/* Delete account */}
            <SubscriptionLayout.DeleteAccount />
        </SubscriptionLayout.Section>

        {/* Invoices */}
        {(isTrial || isStudent) 
            ? <></> 
            : invoiceSelectorWidget(currentState.invoiceSelectorState).map(subscriptionUpdaters.invoiceSelectorState).run(setState)}
    </SubscriptionLayout.Wrapper>
  )
])
}